import { NumberInputProps } from "@mantine/core";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import { useState } from "react";
import { Control, FieldValues, Path, PathValue, useController } from "react-hook-form";

import { InputRightSection } from "#shared/view/designSystem/inputs/InputRightSection";
import { UnitName } from "#shared/view/designSystem/inputs/units";

import { NumberInput } from "./NumberInput";

interface Props<T extends FieldValues> extends Omit<NumberInputProps, "onChange"> {
  name: Path<T>;
  label?: string;
  control: Control<T>;
  required?: boolean;
  precision?: number;
  helper?: string;
  unit?: UnitName;
  onClickWhy?: () => void;
  unitFactors?: { value: number; unit: UnitName }[];
  inlineLabel?: boolean;
  highlight?: boolean;
  defaultUnitFactor?: { value: number; unit: UnitName | null };
  computedValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  companyValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  onChangeFactor?: (unit?: UnitName) => void;
  onChange?: (value: number, oldValue: number) => void;
  rightSectionRef?: React.RefObject<HTMLDivElement | null>;
}

export const NumberInputController = <T extends FieldValues>({
  name,
  control,
  label,
  required,
  disabled,
  precision = 2,
  unit,
  highlight,
  onClickWhy,
  unitFactors,
  defaultUnitFactor,
  computedValue,
  companyValue,
  onChangeFactor,
  onChange,
  rightSectionRef,
  ...props
}: Props<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules: { required } });

  const [selectedFactor, setSelectedFactor] = useState(defaultUnitFactor || { value: 1, unit });

  const handleChangeFactor = (factor: { value: number; unit: UnitName | undefined }) => {
    setSelectedFactor(factor);
    if (onChangeFactor) {
      onChangeFactor(factor.unit);
    }
  };

  return (
    <NumberInput
      {...field}
      label={label}
      min={0}
      highlight={highlight}
      required={required}
      value={isNumber(field.value) ? field.value * selectedFactor.value : undefined}
      disabled={disabled}
      decimalScale={precision}
      decimalSeparator=","
      unit={selectedFactor.unit ?? undefined}
      onClickWhy={onClickWhy}
      // oxlint-disable-next-line typescript/no-base-to-string
      error={error?.message?.toString() || !!error?.type}
      onValueChange={(value) => {
        if (isNil(value.floatValue)) {
          return field.onChange(null);
        }
        field.onChange(value.floatValue / selectedFactor.value);
        onChange?.(value.floatValue, field.value);
      }}
      rightSectionWidth={"auto"}
      rightSection={
        (unitFactors || !isNil(companyValue) || !isNil(computedValue)) && (
          <InputRightSection
            companyValue={companyValue}
            computedValue={computedValue}
            name={name}
            unit={unit}
            unitFactors={unitFactors}
            setSelectedFactor={handleChangeFactor}
            precision={precision}
            rightSectionRef={rightSectionRef}
          />
        )
      }
      {...props}
    />
  );
};
